import React, { useState, useEffect } from "react";
import "./style.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Grid,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Business,
  Description,
  LocationOn,
  AccountBox,
  Payment,
  DateRange,
  Menu,
  Email,
  Image,
  PointOfSale,
  Receipt,
  ReceiptLong,
  Person,
  Link,
  Palette,
} from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AxiosInstance from "../../utils/axiosInstance";
import { getItem } from "../../utils/storage";
import { useUserType } from "../../contexts/UserTypeContext";
import { useAdminDarkMode } from "../../contexts/AdminDarkModeContext";

import ListItemButton from "@mui/material/ListItemButton";

/** @param {"fixed" | "inline"} [props.mobileTrigger] — `inline` keeps the menu control in page flow (avoids covering cards on small screens). */
function SettingSidebar({ mobileTrigger = "fixed" }) {
  const { CompanyUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const theme = useTheme();
  const { darkMode: adminDark } = useAdminDarkMode();
  const isCompanySettingsDark =
    !isDonationMode && (adminDark || theme.palette.mode === "dark");

  const settingNavIconSx = (active) => ({
    color: isDonationMode
      ? active
        ? "#29B3E2"
        : "#063164"
      : isCompanySettingsDark
        ? active
          ? "#ffffff"
          : "#c4cad1"
        : "#063164",
    fontWeight: active ? 600 : 400,
  });

  const settingNavLabelSx = (active) => ({
    margin: 0,
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "visible",
    maxWidth: "100%",
    "& .MuiTypography-root": {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.35,
      overflow: "visible",
      textOverflow: "clip",
    },
    "& .MuiListItemText-primary": {
      fontWeight: active ? 600 : 500,
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.35,
      overflow: "visible",
      textOverflow: "clip",
      color: isDonationMode
        ? active
          ? "#29B3E2"
          : "#2c3e50"
        : isCompanySettingsDark
          ? active
            ? "#e8eaed"
            : "#c4cad1"
          : active
            ? "#063164"
            : "#2c3e50",
    },
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch PricingStructure so we can conditionally show the Disclaimer entry
  // (only relevant when merchant is on "Surcharge (New York)").
  const [pricingStructure, setPricingStructure] = useState("");
  useEffect(() => {
    const companyId = getItem("CompanyId");
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await AxiosInstance.get(
          `/v1/card-fee/company/${companyId}`
        );
        if (!cancelled && res?.data?.statusCode === 200) {
          setPricingStructure(res?.data?.data?.PricingStructure || "");
        }
      } catch (err) {
        // Non-fatal — sidebar still renders without the Disclaimer entry
        if (!cancelled) {
          console.error("SettingSidebar: card-fee fetch failed:", err?.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const showDisclaimer = pricingStructure === "Surcharge (New York)";
  // Initialize expanded state based on screen size - default to true (expanded) for desktop
  const [isExpanded, setIsExpanded] = useState(() => {
    // Default to expanded, will be adjusted by useEffect
    return typeof window !== 'undefined' && window.innerWidth >= 960;
  });

  // Helper function to check if a menu item is active
  const isPathActive = (path) => {
    return location.pathname.includes(path);
  };

  // Update expanded state based on screen size
  useEffect(() => {
    if (isMobile) {
      // Mobile: collapse sidebar (show as button/drawer)
      setIsExpanded(false);
    } else {
      // Tablet and Desktop (md and above): always expand sidebar
      setIsExpanded(true);
    }
  }, [isMobile]);

  // Ensure sidebar is expanded on mount for desktop/tablet
  useEffect(() => {
    // On initial mount, if not mobile, ensure expanded
    if (!isMobile) {
      setIsExpanded(true);
    }
  }, []);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const navigateTo = (path, state) => {
    navigate(CompanyUrl ? `/${CompanyUrl}/${path}` : `/superadmin/${path}`, {
      state: { navigats: ["/index", `/${path}`], ...state },
    });
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const sidebarContent = (
    <Box className={`setting-sidebar-main ${isExpanded ? 'expanded' : ''}`} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box className="sidebar-header" sx={{ flexShrink: 0 }}>
        <Typography variant="h6" className="sidebar-title">
          Settings
        </Typography>
      </Box>

      <List className="sidebar-list" sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "visible" }}>
        {/* Accounts */}
        <ListItemButton
          onClick={() => navigateTo("account")}
          className={`sidebar-item ${isPathActive("account") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("account") 
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("account")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <AccountBox sx={settingNavIconSx(isPathActive("account"))} />
          </ListItemIcon>
          <ListItemText primary="Accounts" sx={settingNavLabelSx(isPathActive("account"))} />
        </ListItemButton>

        {/* Date Format */}
        <ListItemButton
          onClick={() => navigateTo("date-format")}
          className={`sidebar-item ${isPathActive("date-format") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("date-format")
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("date-format")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <DateRange sx={settingNavIconSx(isPathActive("date-format"))} />
          </ListItemIcon>
          <ListItemText
            primary="Date & Time Format"
            sx={settingNavLabelSx(isPathActive("date-format"))}
          />
        </ListItemButton>

        {/* Disclaimer — only when PricingStructure is "Surcharge (New York)" */}
        {showDisclaimer && (
          <ListItemButton
            onClick={() => navigateTo("disclaimer")}
            className={`sidebar-item ${isPathActive("disclaimer") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("disclaimer")
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("disclaimer")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}>
            <ListItemIcon>
              <DescriptionOutlinedIcon sx={settingNavIconSx(isPathActive("disclaimer"))} />
            </ListItemIcon>
            <ListItemText
              primary="Disclaimer"
              sx={settingNavLabelSx(isPathActive("disclaimer"))}
            />
          </ListItemButton>
        )}

        {/* Email Settings */}
        <ListItemButton
          onClick={() => navigateTo("email-settings")}
          className={`sidebar-item ${isPathActive("email-settings") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("email-settings") 
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("email-settings")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <Email sx={settingNavIconSx(isPathActive("email-settings"))} />
          </ListItemIcon>
          <ListItemText
            primary="Email Settings"
            sx={settingNavLabelSx(isPathActive("email-settings"))}
          />
        </ListItemButton>

        {/* Invoice Settings */}
        {!isDonationMode && (
          <ListItemButton
            onClick={() => navigateTo("invoice-settings")}
            className={`sidebar-item ${isPathActive("invoice-settings") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("invoice-settings")
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("invoice-settings")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#063164"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}>
            <ListItemIcon>
              <ReceiptLong sx={{
                color: isPathActive("invoice-settings")
                  ? (isDonationMode ? "#29B3E2" : "#063164")
                  : "#063164",
                fontWeight: isPathActive("invoice-settings") ? 600 : 400
              }} />
            </ListItemIcon>
            <ListItemText
              primary="Invoice Settings"
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: isPathActive("invoice-settings") ? 600 : 500,
                  color: isPathActive("invoice-settings")
                    ? (isDonationMode ? "#29B3E2" : "#063164")
                    : "#2c3e50"
                }
              }}
            />
          </ListItemButton>
        )}

        {/* Logo Settings */}
        <ListItemButton
          onClick={() => navigateTo("logo-setting")}
          className={`sidebar-item ${isPathActive("logo-setting") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("logo-setting") 
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("logo-setting")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <Image sx={settingNavIconSx(isPathActive("logo-setting"))} />
          </ListItemIcon>
          <ListItemText
            primary="Logo Settings"
            sx={settingNavLabelSx(isPathActive("logo-setting"))}
          />
        </ListItemButton>

        {/* Theme & appearance — company admin only (route is /:CompanyUrl/theme) */}
        {CompanyUrl ? (
        <ListItemButton
          onClick={() => navigateTo("theme")}
          className={`sidebar-item ${isPathActive("theme") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("theme")
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("theme")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#063164"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <Palette sx={settingNavIconSx(isPathActive("theme"))} />
          </ListItemIcon>
          <ListItemText
            primary="Theme & appearance"
            sx={settingNavLabelSx(isPathActive("theme"))}
          />
        </ListItemButton>
        ) : null}

        {/* Donation Link - only for donation mode */}
        {isDonationMode && (
          <ListItemButton
            onClick={() => navigateTo("donation-link")}
            className={`sidebar-item ${isPathActive("donation-link") ? "active-donor" : ""}`}
            sx={{
              backgroundColor: isPathActive("donation-link") 
                ? "rgba(41, 179, 226, 0.15)"
                : "transparent",
              borderLeft: isPathActive("donation-link")
                ? "4px solid #29B3E2"
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: "rgba(41, 179, 226, 0.08)"
              }
            }}>
            <ListItemIcon>
              <Link sx={settingNavIconSx(isPathActive("donation-link"))} />
            </ListItemIcon>
            <ListItemText
              primary="Donation Link"
              sx={settingNavLabelSx(isPathActive("donation-link"))}
            />
          </ListItemButton>
        )}

        {/* Name Format */}
        <ListItemButton
          onClick={() => navigateTo("name-format")}
          className={`sidebar-item ${isPathActive("name-format") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("name-format") 
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("name-format")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <Person sx={settingNavIconSx(isPathActive("name-format"))} />
          </ListItemIcon>
          <ListItemText
            primary="Name Format"
            sx={settingNavLabelSx(isPathActive("name-format"))}
          />
        </ListItemButton>

        {/* Payment Processing */}
        <ListItemButton
          onClick={() => navigateTo("payment-processing")}
          className={`sidebar-item ${isPathActive("payment-processing") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("payment-processing")
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("payment-processing")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": { backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)" },
          }}>
          <ListItemIcon>
            <Payment sx={settingNavIconSx(isPathActive("payment-processing"))} />
          </ListItemIcon>
          <ListItemText
            primary="Payment Processing"
            sx={settingNavLabelSx(isPathActive("payment-processing"))}
          />
        </ListItemButton>

        {/* Payment Terminals - only for non-donation mode */}
        {/* {!isDonationMode && ( */}
          <ListItemButton
            onClick={() => navigateTo("payment-terminals")}
            className={`sidebar-item ${isPathActive("payment-terminals") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("payment-terminals") 
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("payment-terminals")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}>
            <ListItemIcon>
              <PointOfSale sx={settingNavIconSx(isPathActive("payment-terminals"))} />
            </ListItemIcon>
            <ListItemText
              primary="Payment Terminals"
              sx={settingNavLabelSx(isPathActive("payment-terminals"))}
            />
          </ListItemButton>
        {/* )} */}

        {/* Materials & Labor - only for non-donation mode */}
        {/* {!isDonationMode && (
          <ListItem
            button
            onClick={() => navigateTo("materials-labor")}
            className={`sidebar-item ${isPathActive("materials-labor") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("materials-labor") 
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("materials-labor")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}
          >
            <ListItemIcon>
              <Business sx={{ 
                color: isPathActive("materials-labor") 
                  ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                  : "#0f2b46",
                fontWeight: isPathActive("materials-labor") ? 600 : 400
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Materials & Labor"
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: isPathActive("materials-labor") ? 600 : 500,
                  color: isPathActive("materials-labor")
                    ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                    : "#2c3e50",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }
              }}
            />
          </ListItem>
        )} */}

        {/* Taxes - only for non-donation mode */}
        {!isDonationMode && (
          <ListItemButton
            onClick={() => navigateTo("taxes")}
            className={`sidebar-item ${isPathActive("taxes") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("taxes")
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("taxes")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}>
            <ListItemIcon>
              <Receipt sx={settingNavIconSx(isPathActive("taxes"))} />
            </ListItemIcon>
            <ListItemText
              primary="Taxes"
              sx={settingNavLabelSx(isPathActive("taxes"))}
            />
          </ListItemButton>
        )}

        {/* Templates */}
        <ListItemButton
          onClick={() => navigateTo("manage-template")}
          className={`sidebar-item ${isPathActive("manage-template") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("manage-template")
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("manage-template")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}>
          <ListItemIcon>
            <Description sx={settingNavIconSx(isPathActive("manage-template"))} />
          </ListItemIcon>
          <ListItemText
            primary="Templates"
            sx={settingNavLabelSx(isPathActive("manage-template"))}
          />
        </ListItemButton>
        
        {/* Transaction Fee */}
        {/* <ListItem
          button
          onClick={() => navigateTo("transaction-settings")}
          className={`sidebar-item ${isPathActive("transaction-settings") ? (isDonationMode ? "active-donor" : "active") : ""}`}
          sx={{
            backgroundColor: isPathActive("transaction-settings") 
              ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
              : "transparent",
            borderLeft: isPathActive("transaction-settings")
              ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
              : "4px solid transparent",
            "&:hover": {
              backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
            }
          }}
        >
          <ListItemIcon>
            <Payment sx={{ 
              color: isPathActive("transaction-settings") 
                ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                : "#0f2b46",
              fontWeight: isPathActive("transaction-settings") ? 600 : 400
            }} />
          </ListItemIcon>
          <ListItemText 
            primary="Transaction Fee"
            sx={{
              "& .MuiListItemText-primary": {
                fontWeight: isPathActive("transaction-settings") ? 600 : 500,
                color: isPathActive("transaction-settings")
                  ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                  : "#2c3e50"
              }
            }}
          />
        </ListItem> */}

        {/* Visits - only for non-donation mode
        {!isDonationMode && (
          <ListItem
            button
            onClick={() => navigateTo("manage-visits")}
            className={`sidebar-item ${isPathActive("manage-visits") ? (isDonationMode ? "active-donor" : "active") : ""}`}
            sx={{
              backgroundColor: isPathActive("manage-visits") 
                ? (isDonationMode ? "rgba(41, 179, 226, 0.15)" : "rgba(6, 49, 100, 0.08)")
                : "transparent",
              borderLeft: isPathActive("manage-visits")
                ? `4px solid ${isDonationMode ? "#29B3E2" : "#0f2b46"}`
                : "4px solid transparent",
              "&:hover": {
                backgroundColor: isDonationMode ? "rgba(41, 179, 226, 0.08)" : "rgba(6, 49, 100, 0.05)"
              }
            }}
          >
            <ListItemIcon>
              <LocationOn sx={{ 
                color: isPathActive("manage-visits") 
                  ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                  : "#0f2b46",
                fontWeight: isPathActive("manage-visits") ? 600 : 400
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Visits"
              sx={{
                "& .MuiListItemText-primary": {
                  fontWeight: isPathActive("manage-visits") ? 600 : 500,
                  color: isPathActive("manage-visits")
                    ? (isDonationMode ? "#29B3E2" : "#0f2b46")
                    : "#2c3e50"
                }
              }}
            />
          </ListItem>
        )} */}
      </List>
    </Box>
  );

  if (isMobile) {
    const triggerClasses = `${
      isDonationMode ? "bg-donor-color" : "bg-orange-color"
    } mobile-menu-button text-white-color${
      mobileTrigger === "inline" ? " mobile-menu-button--inline" : ""
    }`;

    const menuButton = (
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        className={triggerClasses}
        sx={{
          color: "white",
          "&:hover": {
            color: "white",
          },
        }}
      >
        <Menu />
      </IconButton>
    );

    return (
      <>
        {mobileTrigger === "inline" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            {menuButton}
          </Box>
        ) : (
          menuButton
        )}

        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          className="mobile-drawer"
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Grid
      className="desktop-sidebar-container"
      sx={{ 
        margin: 0, 
        padding: 0, 
        width: "100%", 
        height: "100vh", 
        overflowX: "visible",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {sidebarContent}
    </Grid>
  );
}

export default SettingSidebar;
